import { Icon, SearchBox, Table } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { studyPlanTitulationListRequest } from '../api';
import { useFinantialOthersState } from '../hooks/useFinantialOthersState';
import { useStudyPlanTitulationColumns } from '../hooks/useStudyPlanTitulationColumns';
import { StudyPlanTitulationRow } from '../type';

export function FinantialOtherTitulationByStudyPlan({
  academicLevelId,
  academicLevelName,
  onBack,
}: {
  academicLevelId: number;
  academicLevelName: string;
  onBack: () => void;
}) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_FIANANTIAL_OTHERS,
  );
  const { control, watch, setValue } = useForm<{ search: string }>({
    defaultValues: { search: '' },
  });
  const search = watch('search') ?? '';

  const { titulationDraftsState, setTitulationDraftForLevel } =
    useFinantialOthersState();
  // Ref para leer el último cache sin re-disparar el efecto de carga.
  const draftsRef = useRef(titulationDraftsState);
  draftsRef.current = titulationDraftsState;

  const [rows, setRows] = useState<StudyPlanTitulationRow[]>([]);
  const [loading, setLoading] = useState(false);
  // "general para todos" = todos los planes usan el valor global del nivel.
  // Estado propio (no derivado) para poder alternar entre el global y la
  // tabla por plan aunque todavía no exista ningún override.
  const [generalForAll, setGeneralForAll] = useState(true);
  // Valor global del nivel académico (aca_lev_tit_pay).
  const [globalAmount, setGlobalAmount] = useState<number | null>(null);
  const [titulationPaymentId, setTitulationPaymentId] = useState<number | null>(
    null,
  );

  // paymentRequired es DERIVADO, no un toggle manual: en modo "general para
  // todos" lo determina un monto global > 0; en modo por plan, la existencia de
  // algún override > 0. (En el backend, resolveTitulationPayment resuelve el
  // override de plan de forma autónoma; el flag de nivel aplica al monto global.)
  const paymentRequired = generalForAll
    ? (globalAmount ?? 0) > 0
    : rows.some((r) => r.hasOverride && (r.paymentAmount ?? 0) > 0);

  const load = useCallback(async () => {
    // Si el nivel ya tiene un snapshot en memoria (editado o cargado antes),
    // se restaura desde ahí para no perder cambios al volver al listado.
    const cached = draftsRef.current[academicLevelId];
    if (cached) {
      setRows(cached.studyPlans);
      setGeneralForAll(cached.generalForAll);
      setGlobalAmount(cached.paymentAmount);
      setTitulationPaymentId(cached.titulationPaymentId);
      return;
    }
    setLoading(true);
    const { data, error } =
      await studyPlanTitulationListRequest(academicLevelId);
    if (!error && data) {
      setRows(data.studyPlans);
      setGeneralForAll(data.studyPlans.every((r) => !r.hasOverride));
      setGlobalAmount(data.paymentAmount);
      setTitulationPaymentId(data.titulationPaymentId);
    }
    setLoading(false);
  }, [academicLevelId]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = useCallback((studyPlanId: number, hasOverride: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        r.studyPlanId === studyPlanId
          ? {
              ...r,
              hasOverride,
              paymentAmount: hasOverride ? (r.paymentAmount ?? 0) : null,
            }
          : r,
      ),
    );
  }, []);

  const onChangeAmount = useCallback((studyPlanId: number, amount: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.studyPlanId === studyPlanId
          ? { ...r, paymentAmount: amount === '' ? null : Number(amount) }
          : r,
      ),
    );
  }, []);

  const onToggleGeneral = useCallback(() => {
    setGeneralForAll((prev) => {
      const next = !prev;
      if (next) {
        // Volver a "general para todos": limpiar overrides (todos heredan).
        setRows((rs) =>
          rs.map((r) => ({
            ...r,
            hasOverride: false,
            paymentAmount: null,
            disabled: true,
          })),
        );
      } else {
        setRows((rs) =>
          rs.map((r) => ({
            ...r,
            disabled: false,
          })),
        );
      }
      return next;
    });
  }, []);

  const columns = useStudyPlanTitulationColumns({ onToggle, onChangeAmount });

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((r) =>
      `${r.name} ${r.modalityName ?? ''}`.toLowerCase().includes(term),
    );
  }, [rows, search]);

  // El detalle ya no guarda de forma individual: sube un snapshot completo del
  // nivel al estado compartido, para (a) restaurarlo al volver sin perder
  // ediciones y (b) persistirlo en bloque con "GUARDAR CONFIGURACIÓN".
  useEffect(() => {
    if (loading) {
      return;
    }
    setTitulationDraftForLevel(academicLevelId, {
      generalForAll,
      paymentRequired,
      paymentAmount: globalAmount,
      titulationPaymentId,
      studyPlans: rows,
    });
  }, [
    loading,
    academicLevelId,
    generalForAll,
    paymentRequired,
    globalAmount,
    titulationPaymentId,
    rows,
    setTitulationDraftForLevel,
  ]);

  return (
    <div className="w-100 d-flex flex-column">
      <div
        className="w-100 d-flex bg-secondary p-3"
        style={{ borderRadius: '8px 0px 0px 8px' }}
      >
        <button
          type="button"
          className="btn btn-text btn-secondary d-flex align-items-center"
          style={{ gap: 5 }}
          onClick={onBack}
        >
          <Icon name="chevron_left" color="white" size={12} />
          <span className="text-white">
            {t('academicLevelTitulationPayment.detail.back')}
          </span>
        </button>
      </div>
      <div
        className="w-100 d-flex p-3"
        style={{ backgroundColor: '#F7F7F7', height: 84 }}
      >
        <div className="flex-fill d-flex align-items-center">
          <span className="fs-16 fw-700 text-primary text-uppercase">
            {academicLevelName}
          </span>
        </div>
        <div style={{ maxWidth: 350, width: '100%' }}>
          <SearchBox
            name="search"
            label=""
            placeholder={t(
              'academicLevelTitulationPayment.detail.searchPlaceholder',
            )}
            disabled={generalForAll}
            control={control}
            clear={() => setValue('search', '')}
          />
        </div>
      </div>
      <div
        className="w-100 d-flex flex-column p-3"
        style={{
          borderLeft: '1px solid #F7F7F7',
          borderRight: '1px solid #F7F7F7',
          borderBottom: '1px solid #F7F7F7',
        }}
      >
        <div className="d-flex w-100 align-items-center" style={{ gap: 10 }}>
          <div className="flex-fill">
            <label className="g-checkbox-input-container d-flex align-items-center mt-3 mb-4">
              <Input
                className="g-checkbox-input"
                type="checkbox"
                checked={generalForAll}
                onChange={onToggleGeneral}
              />
              <span className="checkmark"></span>
              <span className="ml-2">
                {t('academicLevelTitulationPayment.detail.generalForAll')}
              </span>
            </label>
          </div>
          {!!generalForAll && (
            <div
              className="d-flex w-100 align-items-center justify-content-end"
              style={{ gap: 10 }}
            >
              <span className="fs-14 fw-700 text-primary text-uppercase">
                {t(
                  'academicLevelTitulationPayment.detail.table.paymentAmount',
                )}
              </span>
              <div>
                <Input
                  type="number"
                  min={0}
                  className="border-primary"
                  style={{
                    width: '160px',
                    height: '38px',
                    textAlign: 'center',
                  }}
                  placeholder={t(
                    'academicLevelTitulationPayment.detail.table.paymentAmount',
                  )}
                  value={globalAmount ?? ''}
                  onChange={(e) =>
                    setGlobalAmount(
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div
          className="border border-secondary"
          style={{ borderRadius: 8, overflow: 'hidden' }}
        >
          {generalForAll ? (
            <Table
              columns={columns}
              data={rows}
              isLoadingResults={loading}
              color={'transparent'}
            />
          ) : (
            <Table
              columns={columns}
              data={filteredRows}
              isLoadingResults={loading}
              color={'transparent'}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default FinantialOtherTitulationByStudyPlan;
